import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Checkbox,
  Button,
  Select,
  message,
  Form,
} from 'antd';
import { useTranslation } from 'react-i18next';
import anchorme from 'anchorme';
import { Redirect } from 'react-router';
import axios from 'axios';
import {
  reserveProductToUserAction,
  isUserAlreadyOrderedAction,
} from '../../data/modules/products/products.actions';
import { FlexDivCenter, Text } from '../../components/Typography/Typography';
import { getWeboConfig, isProductHasAgreement } from '../../utils';

export default function LockerForm({ setIsFinished, setActiveProduct }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [deservedForTheProduct, setDeservedForTheProduct] = useState(true);
  const [checked, setChecked] = useState(true);
  const [campuses, setCampuses] = useState([]);
  const [floors, setFloors] = useState([]);
  const [lockers, setLockers] = useState([]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { activeUser } = useSelector((state) => state.auth);
  const products = useSelector((state) => state.products).products
    .filter(p => p.displayInStore && p.availableAmount > 0)
    .filter(p => p.type === 'locker')
    .map(product => {
      let append = {};
      try {
        append = JSON.parse(product.operationalCode);
      } catch (e) {
        console.log(e);
      }
      return { ...product, ...append };
    });


  useEffect(() => {
    const uniqueCampuses = [...new Set(products.map(p => p.campus))];
    if (campuses.length === 0) {
      setCampuses(uniqueCampuses);
    }
  }, [products]);

  function isButtonDisabled() {
    return !checked || !deservedForTheProduct || loading;
  }

  function onCampusChange(campus) {
    const campusFloors = [...new Set(products.filter(p => p.campus === campus).map(p => p.floor))];
    setFloors(campusFloors);
    form.setFieldsValue({ floor: undefined, lockerNumber: undefined });
  }

  function onFloorChange(floor) {
    const floorLockers = products.filter(p => p.floor === floor && p.campus === form.getFieldValue('campus')).map(p => p.lockerNumber);
    setLockers(floorLockers);
    form.setFieldsValue({ lockerNumber: undefined });
  }

  function onLockerChange(lockerNumber) {
    const selectedProduct = products.find(p =>
      p.campus === form.getFieldValue('campus') &&
      p.floor === form.getFieldValue('floor') &&
      p.lockerNumber === lockerNumber
    );

    if (selectedProduct) {
      setLoading(true);
      dispatch(isUserAlreadyOrderedAction(selectedProduct.id))
        .then((res) => {
          setActiveProduct(selectedProduct);
          setDeservedForTheProduct(!res.data.alreadyOrdered);
          setLoading(false);
        })
        .catch(() => {
          setDeservedForTheProduct(false);
          setLoading(false);
        });
      setChecked(!isProductHasAgreement(selectedProduct));
    }
  }

  function onReserveProduct(values) {
    const selectedProduct = products.find(p =>
      p.campus === values.campus &&
      p.floor === values.floor &&
      p.lockerNumber === values.lockerNumber
    );

    if (!selectedProduct) {
      message.error(t('product_not_found'));
      return;
    }

    setActiveProduct(selectedProduct);

    setLoading(true);
    if (selectedProduct.price && getWeboConfig('ENABLE_PAYMENTS')) {
      axios.post('/payments/v1/start', {
        productId: selectedProduct.id,
      }).then(res => {
        const { dynamicPaymentUrl } = res.data;
        window.open(dynamicPaymentUrl, '_blank');
      });
    } else {
      dispatch(
        reserveProductToUserAction({
          productId: selectedProduct.id,
          userId: activeUser.id,
          answers: values,
          type: selectedProduct.type,
        })
      ).then((res) => {
        setLoading(false);
        setIsFinished(res.approvalToken);
        message.success({
          content: t('assign_product_success'),
          style: {
            marginTop: '10vh',
            fontSize: 16,
          },
        });
      });
    }
  }

  if (!deservedForTheProduct) {
    return (
      <FlexDivCenter>
        <Text>
          <span
            dangerouslySetInnerHTML={{
              __html: t('you_cannot_order_service', {
                office_address: getWeboConfig('OFFICE_ADDRESS'),
              }),
            }}
          />
        </Text>
      </FlexDivCenter>
    );
  }

  return (
    <Form
      style={{ marginTop: 30 }}
      form={form}
      name="locker_form"
      className="locker-form"
      onFinish={onReserveProduct}
    >
      <Form.Item
        name="campus"
        rules={[{ required: true, message: t('required', { field: t('campus') }) }]}
      >
        <Select placeholder={t('select_campus')} onChange={onCampusChange}>
          {campuses.map(campus => (
            <Select.Option key={campus} value={campus}>{t(campus)}</Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="floor"
        rules={[{ required: true, message: t('required', { field: t('floor') }) }]}
      >
        <Select placeholder={t('select_floor')} onChange={onFloorChange} disabled={!form.getFieldValue('campus')}>
          {floors.map(floor => (
            <Select.Option key={floor} value={floor}>{floor}</Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="lockerNumber"
        rules={[{ required: true, message: t('required', { field: t('locker_number') }) }]}
      >
        <Select placeholder={t('select_locker')} onChange={onLockerChange} disabled={!form.getFieldValue('floor')}>
          {lockers.map(locker => (
            <Select.Option key={locker} value={locker}>{locker}</Select.Option>
          ))}
        </Select>
      </Form.Item>

      {isProductHasAgreement(products.find(p =>
        p.campus === form.getFieldValue('campus') &&
        p.floor === form.getFieldValue('floor') &&
        p.lockerNumber === form.getFieldValue('lockerNumber')
      )) && (
          <Form.Item name="is_accepted">
            <FlexDivCenter style={{ maxWidth: 350 }}>
              <Text
                dangerouslySetInnerHTML={{
                  __html: anchorme({
                    input: products.find(p =>
                      p.campus === form.getFieldValue('campus') &&
                      p.floor === form.getFieldValue('floor') &&
                      p.lockerNumber === form.getFieldValue('lockerNumber')
                    )?.agreement?.replace('\n', '<br/>') || '',
                    options: {
                      attributes() {
                        return {
                          target: '_blank',
                        };
                      },
                    },
                  }),
                }}
                align="right"
                size="small"
              />
            </FlexDivCenter>
            <Checkbox
              onChange={(e) => setChecked(e.target.checked)}
              style={{ fontSize: 20 }}
            >
              {t('i_have_read')}
            </Checkbox>
          </Form.Item>
        )}
      <FlexDivCenter>
        <Form.Item>
          <Button
            htmlType="submit"
            disabled={isButtonDisabled()}
            style={{
              margin: 15,
              color: isButtonDisabled() ? '' : 'white',
              width: 150,
              background: isButtonDisabled() ? '' : '#1D3E7E',
            }}
            loading={loading}
            type="primary"
          >
            {t(products.find(p =>
              p.campus === form.getFieldValue('campus') &&
              p.floor === form.getFieldValue('floor') &&
              p.lockerNumber === form.getFieldValue('lockerNumber')
            )?.price ? 'buy' : 'reserve')}
          </Button>
        </Form.Item>
      </FlexDivCenter>
    </Form>
  );
}
